import { Action, Core, Expressive } from "@faire/slate/foundation/color";
import { Check } from "@faire/slate/icons/Check";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

const bannerHeaderText = (brandName?: string) => {
  if (brandName) {
    return (
      <StrictLocalMsg
        id="j5xyia"
        defaultMessage="Ready to start buying {brandName} online?"
        values={DEPRECATED_backwardsCompatibleValues({ brandName: brandName })}
      />
    );
  }

  return (
    <StrictLocalMsg
      id="tBGzi+"
      defaultMessage="Ready to start buying wholesale online?"
    />
  );
};

interface IProps {
  brandName?: string;
  onClick: () => void;
}

export const SignUpBanner = ({
  ref,
  ...props
}: IProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <Container ref={ref}>
    <Header
      color={Core.text.primaryInverse}
      align="center"
      variant={{
        mobileAndAbove: "displaySSerifRegular",
        tabletAndAbove: "displayMSerifRegular",
      }}
    >
      {bannerHeaderText(props.brandName)}
    </Header>
    <Spacer height="3x" />
    <Column>
      {useValues().map((value, index) => (
        <React.Fragment key={index}>
          {index === 0 ? null : <Spacer height="2x" />}
          <Value value={value} />
        </React.Fragment>
      ))}
    </Column>
    <Spacer height="3x" />
    <StyledButton onClick={props.onClick}>
      <SignUpButtonContent>
        <StrictLocalMsg
          id="Hlo+Bt"
          defaultMessage="Sign up to Shop"
          description={{
            text: "Button text for retailers to sign up and start shopping on Faire",
          }}
        />
        <StyledArrowRight direction="right" />
      </SignUpButtonContent>
    </StyledButton>
  </Container>
);

const useValues = (): string[] => {
  const { strictLocalize } = useStrictLocalization();
  return [
    strictLocalize({
      id: "kc42Bd",
      defaultMessage: "60-day payment terms",
      description: {
        text: "For retailers | Desktop and moweb | Displays on logged-out brand and category pages | Banner | 1 of 3 value props in a list | Preceded by this header: Ready to shop for your store?” | Followed by this CTA: Sign Up to Shop",
        img: "https://cdn.faire.com/fastly/065ad2201d3daa139867bdf3478b89284ff556807e821ac2fd65deb910eb951d.png",
      },
    }),
    strictLocalize({
      id: "bsEkWZ",
      defaultMessage: "Free returns on all opening orders",
      description: {
        text: "We offer free returns on first order with a brand",
      },
    }),
    strictLocalize({
      id: "DXPfMp",
      defaultMessage: "Unique products curated for your store",
    }),
  ];
};

const Container = styled(Column).attrs({ align: "center" })`
  background-color: ${Expressive.text.yellow};
  width: 100%;
  padding: 48px 32px 56px;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 56px 0 64px;
  }
`;

const Header = styled(Typography)`
  max-width: 450px;
`;

const StyledArrowRight = styled(Arrow)`
  && {
    opacity: 0;
    transition: 0.5s ease;
    position: absolute;
    margin: auto 0;
    top: 0;
    bottom: 0;
    right: 2px;
  }
`;

const SignUpButtonContent = styled.span`
  transition: 0.5s ease;
  position: relative;
`;

const StyledButton = styled(Button)`
  border: 0;
  min-width: 228px;
  width: fit-content;
  color: ${Core.text.primary};
  background-color: white;

  ${SignUpButtonContent} {
    padding: 0 10px;
  }

  &:focus,
  &:focus-within {
    color: ${Core.text.primary};
    background-color: white;
  }

  &:hover {
    color: ${Action.text.hover};
    background-color: white;

    ${SignUpButtonContent} {
      padding: 0 20px 0 0;
    }

    ${StyledArrowRight} {
      color: ${Core.text.primary};
      opacity: 1;
      right: 0;
    }
  }
`;

const StyledRow = styled(Row)`
  max-width: 500px;
`;

const Value: React.FC<{ value: string }> = ({ value }) => (
  <StyledRow align="center">
    <Check color={Core.icon.primaryInverse} />
    <Spacer width="1x" />
    <Typography color={Core.text.primaryInverse}>{value}</Typography>
  </StyledRow>
);
